import { useSelector } from 'react-redux';

import { PaginatedResponseV2 } from 'clipsal-cortex-types/src/common/types';

import { SiteCommissioningImageLinkData } from '../../../api/api-site-commissioning-image-data';
import { InvitedUserData, SiteUser, UserData, UserSaveData } from '../../../api/api-user';
import { baseApi } from '../../../app/baseApi';
import { selectSite } from '../siteSlice';

type AssociateExistingUser = {
  user_id: number;
  clipsal_solar_id: number;
};

type SiteImage = { isLoading: boolean; fileName: string; url: string };

type UserToInvite = Pick<UserData, 'user_email' | 'user_first_name' | 'user_last_name' | 'user_phone' | 'role'> &
  Pick<InvitedUserData, 'clipsal_solar_id'>;

export const siteUsersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSiteUsers: builder.query<SiteUser[], number>({
      query: (siteId) => {
        return {
          url: `/fleet/sites/${siteId}/users`,
          method: 'GET',
        };
      },
      providesTags: (_, __, siteId) => [{ type: 'SiteUsers', siteId }],
    }),
    getInvitedSiteUsers: builder.query<PaginatedResponseV2<InvitedUserData>, number>({
      query: (siteId) => {
        return {
          url: `/fleet/invitations?clipsal_solar_id=${siteId}`,
          method: 'GET',
        };
      },
      providesTags: (_, __, siteId) => [{ type: 'InvitedSiteUsers', siteId }],
    }),
    getSiteImages: builder.query<SiteImage[], number>({
      query: (siteId) => {
        return {
          url: `/fleet/sites/${siteId}/site_image_urls`,
          method: 'GET',
        };
      },
      transformResponse: (images: SiteCommissioningImageLinkData[]) => {
        return images.map(({ file_name: fileName, url }, index) => ({ fileName, url, isLoading: false, index }));
      },
      providesTags: (_, __, siteId) => [{ type: 'SiteImages', siteId }],
    }),
    postNewUser: builder.mutation<InvitedUserData, UserToInvite>({
      query: (body) => {
        return {
          url: `/fleet/invitations`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_, __, { clipsal_solar_id }) => [{ type: 'InvitedSiteUsers', siteId: clipsal_solar_id }],
    }),
    resendInvitation: builder.mutation<void, string>({
      query: (username) => {
        return {
          url: `/fleet/invitations/${username}/resend`,
          method: 'POST',
          body: {},
        };
      },
    }),
    associateExistingUser: builder.mutation<UserSaveData[], AssociateExistingUser>({
      query: (association) => {
        return {
          url: `/fleet/sites/${association.clipsal_solar_id}/users`,
          method: 'POST',
          body: [association],
        };
      },
      invalidatesTags: (_, __, { clipsal_solar_id }) => [{ type: 'SiteUsers', siteId: clipsal_solar_id }],
    }),
    deleteSiteImage: builder.mutation<void, { clipsal_solar_id: number | null; fileName: string; index: number }>({
      query: ({ clipsal_solar_id, fileName }) => {
        return {
          url: `/fleet/sites/${clipsal_solar_id}/delete_site_commissioning_image/${fileName}`,
          method: 'DELETE',
          body: {},
        };
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        // Optimistic update so the user gets fast feedback. The mutation happens in the background.
        const { clipsal_solar_id, index } = arg;

        const updateUiToDeleteInProgress = dispatch(
          siteUsersApi.util.updateQueryData('getSiteImages', clipsal_solar_id || 0, (draft) => {
            draft[index].isLoading = true;
          })
        );

        try {
          await queryFulfilled;
          dispatch(
            siteUsersApi.util.updateQueryData('getSiteImages', clipsal_solar_id || 0, (draft) => {
              // Update this setting in the drafted state. Note that immer provides mutability here!
              draft.splice(index, 1);
            })
          );
        } catch (error) {
          updateUiToDeleteInProgress.undo;
        }
      },
    }),
    removeConfirmedUser: builder.mutation<void, { userId: number; clipsal_solar_id: number }>({
      query: ({ userId, clipsal_solar_id }) => {
        return {
          url: `/fleet/sites/${clipsal_solar_id}/users/${userId}`,
          method: 'DELETE',
          body: {},
        };
      },
      invalidatesTags: (_, __, { clipsal_solar_id }) => [{ type: 'SiteUsers', siteId: clipsal_solar_id }],
    }),
    deleteInvitedUser: builder.mutation<void, { username: string; clipsal_solar_id?: number }>({
      query: ({ username }) => {
        return {
          url: `/fleet/invitations/${username}`,
          method: 'DELETE',
          body: {},
        };
      },
      invalidatesTags: (_, __, { clipsal_solar_id }) => {
        // if clipsal_solar_id is undefined, it means we are deleting a user from the
        // manage users page and no site has been loaded yet
        const invitedSiteUsersInvalidations = clipsal_solar_id
          ? [{ type: 'InvitedSiteUsers' as const, siteId: clipsal_solar_id }]
          : [];
        return [...invitedSiteUsersInvalidations, { type: 'Users', status: 'INVITED' }];
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSiteUsersQuery,
  useGetInvitedSiteUsersQuery,
  useGetSiteImagesQuery,
  usePostNewUserMutation,
  useAssociateExistingUserMutation,
  useResendInvitationMutation,
  useDeleteSiteImageMutation,
  useRemoveConfirmedUserMutation,
  useDeleteInvitedUserMutation,
} = siteUsersApi;

export const useSiteUsers = () => {
  const { clipsal_solar_id } = useSelector(selectSite);
  const { data, isLoading } = useGetSiteUsersQuery(clipsal_solar_id, { skip: !clipsal_solar_id });

  return { users: data || [], isLoading };
};

export const useInvitedSiteUsers = () => {
  const { clipsal_solar_id } = useSelector(selectSite);
  const { data, isLoading } = useGetInvitedSiteUsersQuery(clipsal_solar_id, { skip: !clipsal_solar_id });

  return { invitedUsers: data?.results || [], isLoading };
};

export const useSiteImages = () => {
  const { clipsal_solar_id } = useSelector(selectSite);
  const { data, isLoading, refetch, isFetching } = useGetSiteImagesQuery(clipsal_solar_id);
  return { siteImages: data || [], isLoading, refetch, isFetching };
};
